import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { chatAPI, SOCKET_URL } from '../services/api';
import { chatService } from '../services/chatService';
import { ArrowLeft, Send, Trash2, Image, Video, FileText, Play, Loader2, MessageSquare } from 'lucide-react';
import { ChatSkeleton } from '../components/skeletons';

const S = {
  container: {
    display: 'flex',
    height: '100%',
    width: '100%',
    background: '#FAF9F6',
    fontFamily: 'var(--font-sans, "Outfit", sans-serif)',
    overflow: 'hidden',
  },
  leftCol: (hideOnMobile) => ({
    flex: '0 0 350px',
    borderRight: '1px solid var(--border)',
    background: '#fff',
    display: hideOnMobile ? 'none' : 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '350px',
    '@media (max-width: 768px)': {
      flex: '1',
      maxWidth: '100%',
    }
  }),
  rightCol: (hideOnMobile) => ({
    flex: 1,
    display: hideOnMobile ? 'none' : 'flex',
    flexDirection: 'column',
    background: '#FAF9F6',
    position: 'relative',
  }),
  listHeader: {
    padding: '20px 16px',
    borderBottom: '1px solid var(--border)',
    background: '#fff',
  },
  listTitle: {
    fontFamily: 'var(--font-serif)',
    fontSize: 24,
    fontWeight: 800,
    color: '#1B5C3A',
    marginBottom: 6,
  },
  searchBox: {
    width: '100%',
    padding: '10px 14px',
    borderRadius: 8,
    border: '1.5px solid var(--border)',
    fontSize: 13,
    background: '#F9F9F9',
    outline: 'none',
    boxSizing: 'border-box',
  },
  roomsScroll: {
    flex: 1,
    overflowY: 'auto',
  },
  roomCard: (active) => ({
    padding: '16px',
    borderBottom: '1px solid #F3F4F6',
    display: 'flex',
    gap: 12,
    cursor: 'pointer',
    background: active ? '#F0F7F4' : '#fff',
    transition: 'background 0.2s ease',
    borderLeft: active ? '4px solid #1B5C3A' : '4px solid transparent',
  }),
  avatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    background: '#E8F5EE',
    color: '#1B5C3A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    fontWeight: 700,
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  roomMeta: {
    flex: 1,
    minWidth: 0,
  },
  roomHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 4,
  },
  partnerName: {
    fontSize: 14,
    fontWeight: 700,
    color: 'var(--char)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  timeAgo: {
    fontSize: 11,
    color: 'var(--muted)',
    flexShrink: 0,
  },
  demandCtx: {
    fontSize: 11,
    fontWeight: 600,
    color: '#B88200',
    background: '#FFF8E7',
    padding: '2px 6px',
    borderRadius: 4,
    display: 'inline-block',
    marginBottom: 6,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  lastMsg: {
    fontSize: 12,
    color: 'var(--muted)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chatHeader: {
    padding: '16px 20px',
    background: '#fff',
    borderBottom: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    marginRight: 12,
    color: '#1B5C3A',
    padding: 4,
    borderRadius: '50%',
    transition: 'background 0.2s',
  },
  chatInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerName: {
    fontSize: 16,
    fontWeight: 800,
    color: 'var(--char)',
  },
  headerRole: {
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: '#B88200',
    fontWeight: 700,
    marginTop: 2,
  },
  chatDemandTag: {
    fontSize: 12,
    color: 'var(--muted)',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  chatScroll: {
    flex: 1,
    padding: '24px 20px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  bubbleWrapper: (isMe) => ({
    display: 'flex',
    justifyContent: isMe ? 'flex-end' : 'flex-start',
  }),
  bubble: (isMe) => ({
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
    background: isMe ? '#1B5C3A' : '#fff',
    color: isMe ? '#fff' : 'var(--char)',
    fontSize: 14,
    lineHeight: 1.5,
    boxShadow: isMe ? '0 2px 6px rgba(27,92,58,0.15)' : '0 2px 6px rgba(0,0,0,0.03)',
    border: isMe ? 'none' : '1.5px solid var(--border)',
    wordBreak: 'break-word',
  }),
  bubbleTime: {
    fontSize: 10,
    marginTop: 4,
    textAlign: 'right',
    opacity: 0.75,
  },
  typingIndicator: {
    fontSize: 12,
    color: 'var(--muted)',
    fontStyle: 'italic',
    padding: '4px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  inputArea: {
    padding: '16px 20px',
    background: '#fff',
    borderTop: '1px solid var(--border)',
    display: 'flex',
    gap: 12,
    alignItems: 'center',
  },
  input: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: 10,
    border: '1.5px solid var(--border)',
    outline: 'none',
    fontSize: 14,
    background: '#F9F9F9',
    transition: 'all 0.2s ease',
  },
  sendBtn: {
    background: '#1B5C3A',
    color: '#fff',
    border: 'none',
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 18,
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(27,92,58,0.2)',
  },
  noActiveChat: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--muted)',
    padding: 40,
    textAlign: 'center',
  },
  chatBadge: {
    background: '#FFF8E7',
    color: '#B88200',
    fontSize: 11,
    padding: '3px 8px',
    borderRadius: 8,
    fontWeight: 700,
    textTransform: 'uppercase',
  },
  unreadBadge: {
    background: '#25D366',
    color: '#fff',
    fontSize: 10,
    minWidth: 18,
    height: 18,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    padding: '0 4px',
    boxSizing: 'border-box',
    boxShadow: '0 2px 4px rgba(37,211,102,0.3)',
    flexShrink: 0,
  },
  plusBtn: {
    background: '#E8F5EE',
    color: '#1B5C3A',
    border: 'none',
    width: 44,
    height: 44,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: 20,
    transition: 'all 0.2s ease',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  dropdownWrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  dropdownMenu: {
    position: 'absolute',
    bottom: '55px',
    left: '0',
    backgroundColor: '#fff',
    minWidth: '160px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    borderRadius: '8px',
    padding: '8px 0',
    zIndex: 100,
    border: '1px solid var(--border)',
    display: 'flex',
    flexDirection: 'column',
  },
  dropdownItem: {
    padding: '10px 16px',
    fontSize: '13px',
    color: 'var(--char)',
    cursor: 'pointer',
    textAlign: 'left',
    background: 'none',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background 0.2s ease',
  },
  imageMsg: {
    maxWidth: '100%',
    maxHeight: '250px',
    borderRadius: '8px',
    cursor: 'pointer',
    marginTop: '6px',
  },
  videoMsg: {
    maxWidth: '100%',
    maxHeight: '250px',
    borderRadius: '8px',
    marginTop: '6px',
  },
  docMsgCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    backgroundColor: '#F3F4F6',
    borderRadius: '8px',
    marginTop: '6px',
    color: 'var(--char)',
    textDecoration: 'none',
    border: '1px solid var(--border)',
    transition: 'background 0.2s ease',
  },
  docIcon: {
    fontSize: '24px',
  },
  docInfo: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  docName: {
    fontSize: '13px',
    fontWeight: '600',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  docSize: {
    fontSize: '10px',
    color: 'var(--muted)',
  },
  pendingPreviewContainer: {
    padding: '10px 16px',
    backgroundColor: '#F3F4F6',
    borderTop: '1px solid var(--border)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    position: 'relative',
  },
  pendingImagePreview: {
    width: '50px',
    height: '50px',
    borderRadius: '4px',
    objectFit: 'cover',
  },
  pendingFileBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#fff',
    borderRadius: '6px',
    border: '1px solid var(--border)',
  },
  pendingRemoveBtn: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: '#E5E7EB',
    border: 'none',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontSize: '12px',
  },
  previewUploadOverlay: {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '50px',
    height: '50px',
    borderRadius: '4px',
    backgroundColor: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: '16px',
  },
  roomDeleteBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'var(--muted)',
    padding: '2px 4px',
    opacity: 0.6,
    transition: 'opacity 0.2s',
    ':hover': {
      opacity: 1,
      color: '#F43F5E',
    }
  },
  deleteMsgBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '11px',
    color: 'rgba(255,255,255,0.6)',
    padding: '1px 3px',
    opacity: 0.8,
    transition: 'opacity 0.2s',
    marginLeft: 'auto',
  }
};

function formatTime(d) {
  if (!d) return '';
  const date = new Date(d);
  return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function timeAgo(date) {
  if (!date) return '';
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function formatChatTimestamp(dateVal) {
  if (!dateVal) return '';
  const date = new Date(dateVal);
  const now = new Date();

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (compareDate.getTime() === today.getTime()) {
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
  } else if (compareDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  } else {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }
}

export default function Messages() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const offerIdParam = searchParams.get('offerId');
  const demandIdParam = searchParams.get('demandId');

  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isPartnerTyping, setIsPartnerTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locFilter, setLocFilter] = useState('all');
  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const activeRoomIdRef = useRef(null);
  const processedMessageIdsRef = useRef(new Set());
  const [showDropdown, setShowDropdown] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewMedia, setPreviewMedia] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);

  // Close dropdown on click outside
  useEffect(() => {
    const handleOutsideClick = () => {
      setShowDropdown(false);
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  // Responsive mode flags
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'chat'

  // Sync active room ID ref
  useEffect(() => {
    activeRoomIdRef.current = activeRoom?._id;
  }, [activeRoom]);

  // 1. Fetch Rooms & handle potential query param redirect
  useEffect(() => {
    const fetchRooms = async () => {
      try {
        const res = await chatAPI.rooms();
        const allRooms = res.data.data || [];
        setRooms(allRooms);

        if (offerIdParam) {
          // Check if room for this offer exists, otherwise create/get it
          const initRes = await chatAPI.roomForOffer(offerIdParam);
          const targetedRoom = initRes.data.data;

          if (targetedRoom) {
            // Add to room list if not present
            if (!allRooms.some(r => r._id === targetedRoom._id)) {
              setRooms(prev => [targetedRoom, ...prev]);
            }
            setActiveRoom(targetedRoom);
            setViewMode('chat');
            setRooms(prev => prev.map(r => r._id === targetedRoom._id ? { ...r, unreadCount: 0 } : r));
            chatAPI.markRead(targetedRoom._id).catch(err => console.error('Failed to mark read:', err));
          }
          // Clear query param so refreshes don't re-initiate
          setSearchParams({});
        } else if (demandIdParam) {
          // Check if room for this demand exists, otherwise create/get it
          const initRes = await chatAPI.roomForDemand(demandIdParam);
          const targetedRoom = initRes.data.data;

          if (targetedRoom) {
            // Add to room list if not present
            if (!allRooms.some(r => r._id === targetedRoom._id)) {
              setRooms(prev => [targetedRoom, ...prev]);
            }
            setActiveRoom(targetedRoom);
            setViewMode('chat');
            setRooms(prev => prev.map(r => r._id === targetedRoom._id ? { ...r, unreadCount: 0 } : r));
            chatAPI.markRead(targetedRoom._id).catch(err => console.error('Failed to mark read:', err));
          }
          // Clear query param so refreshes don't re-initiate
          setSearchParams({});
        }
      } catch (err) {
        console.error('Failed to load chat rooms:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRooms();
  }, [offerIdParam, demandIdParam, setSearchParams]);

  // 2. Setup Socket once on mount or when user changes to listen for messages in real-time
  useEffect(() => {
    if (!user) return;

    // Connect socket to server, joining the user's personal room
    chatService.connect(activeRoomIdRef.current, user._id);
    // Register dynamic listeners
    const unsubscribeMessage = chatService.onMessage((newMsg) => {
      // Prevent duplicate processing of the same message from dual channels (room + user personal channel)
      if (processedMessageIdsRef.current.has(newMsg._id)) return;
      processedMessageIdsRef.current.add(newMsg._id);

      // Only append if it belongs to this active room
      if (newMsg.chatRoomId === activeRoomIdRef.current) {
        setMessages(prev => {
          if (prev.some(m => m._id === newMsg._id)) return prev;
          return [...prev, newMsg];
        });
        scrollToBottom();
        // If we receive a message in the active room from the partner, mark it read immediately
        if (String(newMsg.senderId) !== String(user?._id)) {
          chatService.markRead(activeRoomIdRef.current, user?._id);
        }
      }
      let displayLastMsg = newMsg.content;
      if (!displayLastMsg && newMsg.mediaType) {
        if (newMsg.mediaType === 'image') displayLastMsg = '🖼️ [Photo]';
        else if (newMsg.mediaType === 'video') displayLastMsg = '🎥 [Video]';
        else if (newMsg.mediaType === 'document') displayLastMsg = '📄 [Document]';
      }

      // Update last message and unread count in the room list
      setRooms(prev => {
        const roomExists = prev.some(r => r._id === newMsg.chatRoomId);
        if (!roomExists) {
          chatAPI.rooms().then(res => setRooms(res.data.data || []));
          return prev;
        }
        return prev.map(r => {
          if (r._id === newMsg.chatRoomId) {
            const isMsgFromMe = String(newMsg.senderId) === String(user?._id);
            const isActive = r._id === activeRoomIdRef.current;
            const newUnreadCount = (isMsgFromMe || isActive) ? 0 : (r.unreadCount || 0) + 1;
            return {
              ...r,
              lastMessage: displayLastMsg,
              lastActivity: newMsg.timestamp,
              unreadCount: newUnreadCount
            };
          }
          return r;
        }).sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity));
      });
    });

    const unsubscribeTyping = chatService.onTyping((typingData) => {
      // Typing indicator
      if (typingData.senderId !== user?._id) {
        setIsPartnerTyping(typingData.isTyping);
      }
    });

    const unsubscribeDelete = chatService.onDelete((deletedData) => {
      if (deletedData.chatRoomId === activeRoomIdRef.current) {
        setMessages(prev => prev.map(m => {
          if (m._id === deletedData.messageId) {
            return { ...m, isDeleted: true, content: '🚫 This message was deleted', mediaUrl: undefined, mediaType: undefined };
          }
          return m;
        }));
      }
      setRooms(prev => prev.map(r => {
        if (r._id === deletedData.chatRoomId) {
          return { ...r, lastMessage: '🚫 This message was deleted' };
        }
        return r;
      }));
    });

    const unsubscribeMarkRead = chatService.onMarkRead((markReadData) => {
      if (markReadData.roomId) {
        setRooms(prev => prev.map(r => r._id === markReadData.roomId ? { ...r, unreadCount: 0 } : r));
      }
    });

    return () => {
      unsubscribeMessage();
      unsubscribeTyping();
      unsubscribeDelete();
      unsubscribeMarkRead();
    };
  }, [user]);

  // 3. Manage active room joining/leaving and message fetching
  useEffect(() => {
    if (!activeRoom) return;

    // Load message history
    chatAPI.messages(activeRoom._id)
      .then(res => {
        setMessages(res.data.data || []);
        scrollToBottom();
      })
      .catch(err => console.error('Failed to load history:', err));

    // Join the active chat room via socket
    chatService.joinRoom(activeRoom._id);

    // Reset partner typing state
    setIsPartnerTyping(false);

    return () => {
      // Leave the active chat room via socket when changing activeRoom or unmounting
      chatService.leaveRoom(activeRoom._id);
    };
  }, [activeRoom]);

  // Helper to scroll
  const scrollToBottom = () => {
    setTimeout(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 50);
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!activeRoom || !user) return;

    const messageText = text.trim();
    if (!messageText && !pendingFile) return;

    if (pendingFile && pendingFile.uploading) {
      alert('Please wait for the file upload to complete.');
      return;
    }

    try {
      let mediaUrl = null;
      let mediaType = null;
      let displayContent = messageText;

      if (pendingFile) {
        mediaUrl = pendingFile.url;
        mediaType = pendingFile.mediaType;
        if (!displayContent) {
          displayContent = pendingFile.name;
        }
      }

      chatService.sendMessage(activeRoom._id, user._id, displayContent || '', mediaUrl, mediaType);
      setText('');
      setPendingFile(null);

      // Stop typing immediately
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      chatService.sendTyping(activeRoom._id, user._id, false);
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const handleInputChange = (e) => {
    setText(e.target.value);

    if (!activeRoom || !user) return;

    // Emit typing start
    chatService.sendTyping(activeRoom._id, user._id, true);

    // Debounce typing stop
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      chatService.sendTyping(activeRoom._id, user._id, false);
    }, 2000);
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file || !activeRoom || !user) return;

    // Client-side Validations
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    const size = file.size;

    if (type === 'image') {
      if (!['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(ext)) {
        alert('Invalid file format. Please choose a JPEG, PNG, GIF, or WEBP image.');
        return;
      }
      if (size > 10 * 1024 * 1024) {
        alert('Image is too large. Maximum size allowed is 10MB.');
        return;
      }
    } else if (type === 'video') {
      if (!['.mp4', '.webm', '.mov', '.m4v', '.avi', '.mkv'].includes(ext)) {
        alert('Invalid video format. Please select a valid video (MP4, WEBM, MOV, etc.).');
        return;
      }
      if (size > 50 * 1024 * 1024) {
        alert('Video is too large. Maximum size allowed is 50MB.');
        return;
      }
    } else if (type === 'document') {
      if (!['.pdf', '.docx', '.xlsx', '.txt'].includes(ext)) {
        alert('Invalid document format. Only PDF, DOCX, XLSX, and TXT are allowed.');
        return;
      }
      if (size > 20 * 1024 * 1024) {
        alert('Document is too large. Maximum size allowed is 20MB.');
        return;
      }
    }

    // Set pending file state for staging with uploading = true
    const previewUrl = type === 'image' ? URL.createObjectURL(file) : null;
    setPendingFile({ name: file.name, type, previewUrl, uploading: true, url: null, mediaType: null });
    setShowDropdown(false);

    // Clear the input
    e.target.value = null;

    try {
      const res = await chatAPI.uploadFile(file);
      if (res.data?.success && res.data?.data) {
        const media = res.data.data;
        setPendingFile({
          name: file.name,
          type,
          previewUrl,
          uploading: false,
          url: media.url,
          mediaType: media.type
        });
      } else {
        alert(res.data?.message || 'Failed to upload file.');
        setPendingFile(null);
      }
    } catch (err) {
      console.error('File upload failed:', err);
      alert('An error occurred during file upload.');
      setPendingFile(null);
    }
  };

  const handleDeleteRoom = (roomId) => {
    setDeleteTarget({
      type: 'room',
      id: roomId,
      title: 'Delete Chat Room?',
      desc: 'Are you sure you want to delete this chat room? This action cannot be undone.'
    });
  };

  const handleDeleteMessage = (messageId) => {
    setDeleteTarget({
      type: 'message',
      id: messageId,
      title: 'Delete Message?',
      desc: 'Are you sure you want to delete this message for everyone? This action cannot be undone.'
    });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      if (deleteTarget.type === 'room') {
        await chatAPI.deleteRoom(deleteTarget.id);
        setRooms(prev => prev.filter(r => r._id !== deleteTarget.id));
        if (activeRoom?._id === deleteTarget.id) {
          setActiveRoom(null);
        }
      } else if (deleteTarget.type === 'message') {
        await chatAPI.deleteMessage(deleteTarget.id);
        setMessages(prev => prev.map(m => {
          if (m._id === deleteTarget.id) {
            return { ...m, isDeleted: true, content: '🚫 This message was deleted', mediaUrl: undefined, mediaType: undefined };
          }
          return m;
        }));
      }
      setDeleteTarget(null);
    } catch (err) {
      console.error('Failed to execute delete:', err);
      alert('Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  // Compute partner info for a room
  const getPartnerInfo = (room) => {
    if (!room || !user) return {};
    const isUserBuyer = user.role === 'buyer';
    return isUserBuyer
      ? { name: room.vendorId?.fullName || 'Vendor', role: 'vendor' }
      : { name: room.buyerId?.fullName || 'Buyer', role: 'buyer' };
  };

  // Helper to determine if a room has a local demand
  const isLocalDemand = (room) => {
    if (!room?.demandId || !user?.city) return false;
    const demandCity = room.demandId.location?.city?.toLowerCase()?.trim();
    const userCity = user.city?.toLowerCase()?.trim();
    return demandCity && userCity && demandCity === userCity;
  };

  // Filter conversations
  const filteredRooms = rooms.filter(r => {
    const info = getPartnerInfo(r);
    const nameMatch = info.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const demandMatch = r.demandId?.title?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!(nameMatch || demandMatch)) return false;

    if (locFilter === 'local') {
      return isLocalDemand(r);
    } else if (locFilter === 'outside') {
      return !isLocalDemand(r);
    }
    return true;
  });

  const selectRoom = (room) => {
    setActiveRoom(room);
    setViewMode('chat');
    // Instantly reset unreadCount in React state
    setRooms(prev => prev.map(r => r._id === room._id ? { ...r, unreadCount: 0 } : r));
    // Explicitly call backend REST markRead API to clear it on backend
    chatAPI.markRead(room._id).catch(err => console.error('Failed to mark read:', err));
  };

  if (loading) {
    return <ChatSkeleton />;
  }

  const isMobile = window.innerWidth <= 768;

  return (
    <div style={S.container}>
      {/* LEFT COLUMN: Rooms List */}
      <div style={S.leftCol(isMobile && viewMode === 'chat')}>
        <div style={S.listHeader}>
          <div style={S.listTitle}>Messages</div>
          <input
            type="text"
            placeholder="Search chat or demand..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={S.searchBox}
          />
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            <button
              onClick={() => setLocFilter('all')}
              style={{
                flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                border: locFilter === 'all' ? '1.5px solid #1B5C3A' : '1.5px solid var(--border, #E5E7EB)',
                background: locFilter === 'all' ? '#E8F5EE' : '#fff',
                color: locFilter === 'all' ? '#1B5C3A' : 'var(--muted, #6B7280)',
                transition: 'all 0.15s ease'
              }}
            >
              All Chats
            </button>
            <button
              onClick={() => setLocFilter('local')}
              style={{
                flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                border: locFilter === 'local' ? '1.5px solid #1B5C3A' : '1.5px solid var(--border, #E5E7EB)',
                background: locFilter === 'local' ? '#E8F5EE' : '#fff',
                color: locFilter === 'local' ? '#1B5C3A' : 'var(--muted, #6B7280)',
                transition: 'all 0.15s ease'
              }}
            >
              📍 Local ({user?.city || 'My City'})
            </button>
            <button
              onClick={() => setLocFilter('outside')}
              style={{
                flex: 1, padding: '6px 10px', borderRadius: 8, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                border: locFilter === 'outside' ? '1.5px solid #1B5C3A' : '1.5px solid var(--border, #E5E7EB)',
                background: locFilter === 'outside' ? '#E8F5EE' : '#fff',
                color: locFilter === 'outside' ? '#1B5C3A' : 'var(--muted, #6B7280)',
                transition: 'all 0.15s ease'
              }}
            >
              Outside City
            </button>
          </div>
        </div>
        <div style={S.roomsScroll}>
          {filteredRooms.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
              No chats found.
            </div>
          ) : (
            filteredRooms.map(room => {
              const partner = getPartnerInfo(room);
              const isActive = activeRoom?._id === room._id;
              return (
                <div
                  key={room._id}
                  onClick={() => selectRoom(room)}
                  style={S.roomCard(isActive)}
                >
                  <div style={S.avatar}>
                    {(partner.name || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div style={S.roomMeta}>
                    <div style={S.roomHeader}>
                      <span style={S.partnerName}>{partner.name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={S.timeAgo}>{formatChatTimestamp(room.lastActivity)}</span>
                        <button
                          title="Delete Chat"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteRoom(room._id);
                          }}
                          style={S.roomDeleteBtn}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                      <div style={S.demandCtx}>{room.demandId?.title || room.demandId?.name || 'Lead Details'}</div>
                      {room.demandId?.location?.city && (
                        <span style={{
                          fontSize: 10,
                          fontWeight: 700,
                          padding: '1px 6px',
                          borderRadius: 4,
                          background: isLocalDemand(room) ? '#E8F5EE' : '#F3F4F6',
                          color: isLocalDemand(room) ? '#1B5C3A' : '#6B7280',
                          border: isLocalDemand(room) ? '1px solid #A7C9B5' : '1px solid #E5E7EB'
                        }}>
                          {room.demandId.location.city}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                      <div style={{ ...S.lastMsg, flex: 1, marginRight: 8 }}>
                        {room.lastMessage || 'Start a conversation...'}
                      </div>
                      {room.unreadCount > 0 && (
                        <div style={S.unreadBadge}>
                          {room.unreadCount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* RIGHT COLUMN: Chat Area */}
      <div style={S.rightCol(isMobile && viewMode === 'list')}>
        {activeRoom ? (
          <>
            {/* Chat Room Header */}
            <div style={S.chatHeader}>
              <div style={S.chatInfo}>
                {isMobile && (
                  <button
                    onClick={() => {
                      setViewMode('list');
                      setActiveRoom(null);
                    }}
                    style={S.backBtn}
                    title="Back to Chats"
                  >
                    <ArrowLeft size={20} />
                  </button>
                )}
                <div style={S.avatar}>
                  {getPartnerInfo(activeRoom).name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={S.headerName}>{getPartnerInfo(activeRoom).name}</div>
                  <div style={S.headerRole}>
                    {getPartnerInfo(activeRoom).role === 'vendor' ? 'Vendor Partner' : 'Buyer Client'}
                  </div>
                </div>
              </div>
              <div style={S.chatDemandTag}>
                <span style={S.chatBadge}>Demand Context</span>
                <strong style={{ color: 'var(--char)' }}>{activeRoom.demandId?.title || activeRoom.demandId?.name}</strong>
                {activeRoom.demandId?.price != null && (
                  <span style={{ fontSize: 11, background: '#E8F5EE', color: '#1B5C3A', padding: '3px 8px', borderRadius: 8, fontWeight: 700 }}>
                    Buyer Price: ₹{activeRoom.demandId.price.toLocaleString('en-IN')}
                  </span>
                )}
                {activeRoom.offerId?.price != null && (
                  <span style={{ fontSize: 11, background: '#FFF4D6', color: '#8A5A00', padding: '3px 8px', borderRadius: 8, fontWeight: 700 }}>
                    Vendor Price: ₹{activeRoom.offerId.price.toLocaleString('en-IN')}
                  </span>
                )}
              </div>
            </div>

            {/* Messages Feed */}
            <div ref={scrollRef} style={S.chatScroll}>
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--muted)', fontSize: 13, padding: 20 }}>
                  👋 Say hi to initiate your deal conversation!
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMe = String(msg.senderId) === String(user?._id);
                  return (
                    <div key={msg._id || i} style={S.bubbleWrapper(isMe)}>
                      <div style={S.bubble(isMe)}>
                        {msg.isDeleted ? (
                          <div style={{ fontStyle: 'italic', color: isMe ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.4)', fontSize: 13, display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🚫 This message was deleted</span>
                          </div>
                        ) : (
                          <>
                            {msg.content && <div>{msg.content}</div>}
                            {msg.mediaUrl && (
                              <div style={{ marginTop: msg.content ? 6 : 0 }}>
                                {msg.mediaType === 'image' && (
                                  <img
                                    src={msg.mediaUrl.startsWith('/') ? `${SOCKET_URL}${msg.mediaUrl}` : msg.mediaUrl}
                                    alt="Chat Attachment"
                                    style={S.imageMsg}
                                    onClick={() => setPreviewMedia({
                                      url: msg.mediaUrl.startsWith('/') ? `${SOCKET_URL}${msg.mediaUrl}` : msg.mediaUrl,
                                      type: 'image',
                                      name: msg.content || 'Photo'
                                    })}
                                  />
                                )}
                                {msg.mediaType === 'video' && (
                                  <div
                                    style={{ position: 'relative', cursor: 'pointer', display: 'inline-block', maxWidth: '100%' }}
                                    onClick={() => setPreviewMedia({
                                      url: msg.mediaUrl.startsWith('/') ? `${SOCKET_URL}${msg.mediaUrl}` : msg.mediaUrl,
                                      type: 'video',
                                      name: msg.content || 'Video'
                                    })}
                                  >
                                    <video
                                      src={msg.mediaUrl.startsWith('/') ? `${SOCKET_URL}${msg.mediaUrl}` : msg.mediaUrl}
                                      style={S.videoMsg}
                                    />
                                    <div style={{
                                      position: 'absolute', top: 6, left: 0, right: 0, bottom: 0,
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      background: 'rgba(0,0,0,0.2)', borderRadius: 8, height: 'calc(100% - 6px)'
                                    }}>
                                      <Play size={36} color="#fff" fill="#fff" style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                                    </div>
                                  </div>
                                )}
                                {msg.mediaType === 'document' && (
                                  <div
                                    onClick={(e) => {
                                      e.preventDefault();
                                      setPreviewMedia({
                                        url: msg.mediaUrl.startsWith('/') ? `${SOCKET_URL}${msg.mediaUrl}` : msg.mediaUrl,
                                        type: 'document',
                                        name: msg.content || 'Document'
                                      });
                                    }}
                                    style={{ ...S.docMsgCard, cursor: 'pointer' }}
                                  >
                                    <span style={{ ...S.docIcon, display: 'flex', alignItems: 'center' }}>
                                      <FileText size={24} color="#1B5C3A" />
                                    </span>
                                    <div style={S.docInfo}>
                                      <span style={S.docName}>{msg.content || 'Document'}</span>
                                      <span style={S.docSize}>Click to preview</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4, width: '100%' }}>
                          <span style={S.bubbleTime}>{formatTime(msg.timestamp || msg.createdAt)}</span>
                          {isMe && !msg.isDeleted && (
                            <button
                              type="button"
                              title="Delete for Everyone"
                              onClick={() => handleDeleteMessage(msg._id)}
                              style={S.deleteMsgBtn}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Typing Indicator */}
            {isPartnerTyping && (
              <div style={S.typingIndicator}>
                <div className="spinner" style={{ width: 12, height: 12 }} />
                <span>{getPartnerInfo(activeRoom).name} is typing...</span>
              </div>
            )}

            {/* Pending File Preview Area */}
            {pendingFile && (
              <div style={S.pendingPreviewContainer}>
                {pendingFile.type === 'image' ? (
                  <div style={{ position: 'relative' }}>
                    <img src={pendingFile.previewUrl} alt="Pending Preview" style={S.pendingImagePreview} />
                    {pendingFile.uploading && (
                      <div style={S.previewUploadOverlay}>⏳</div>
                    )}
                  </div>
                ) : (
                  <div style={S.pendingFileBadge}>
                    <span>{pendingFile.uploading ? '⏳' : (pendingFile.type === 'video' ? '🎥' : '📄')}</span>
                    <span style={{ fontSize: 13, fontWeight: '600', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {pendingFile.name} {pendingFile.uploading ? '(Uploading...)' : ''}
                    </span>
                  </div>
                )}
                <button
                  type="button"
                  style={S.pendingRemoveBtn}
                  onClick={() => setPendingFile(null)}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Input Bar */}
            <form onSubmit={handleSend} style={S.inputArea}>
              {/* Invisible File Inputs */}
              <input
                type="file"
                ref={imageInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={(e) => handleFileUpload(e, 'image')}
              />
              <input
                type="file"
                ref={videoInputRef}
                style={{ display: 'none' }}
                accept="video/*"
                onChange={(e) => handleFileUpload(e, 'video')}
              />
              <input
                type="file"
                ref={documentInputRef}
                style={{ display: 'none' }}
                accept=".pdf,.docx,.xlsx,.txt"
                onChange={(e) => handleFileUpload(e, 'document')}
              />

              {/* Plus Button with Dropdown */}
              <div style={S.dropdownWrapper} onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => setShowDropdown(!showDropdown)}
                  style={S.plusBtn}
                  disabled={uploading}
                >
                  {uploading ? <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} /> : '+'}
                </button>
                {showDropdown && (
                  <div style={S.dropdownMenu}>
                    <button
                      type="button"
                      style={S.dropdownItem}
                      onClick={() => imageInputRef.current?.click()}
                    >
                      <Image size={16} color="#1B5C3A" /> Photo
                    </button>
                    <button
                      type="button"
                      style={S.dropdownItem}
                      onClick={() => videoInputRef.current?.click()}
                    >
                      <Video size={16} color="#1B5C3A" /> Video
                    </button>
                    <button
                      type="button"
                      style={S.dropdownItem}
                      onClick={() => documentInputRef.current?.click()}
                    >
                      <FileText size={16} color="#1B5C3A" /> Document
                    </button>
                  </div>
                )}
              </div>

              <input
                type="text"
                value={text}
                onChange={handleInputChange}
                placeholder={uploading ? "Uploading file..." : "Type a message..."}
                style={S.input}
                disabled={uploading}
              />
              <button type="submit" style={S.sendBtn} disabled={uploading}>
                <Send size={18} />
              </button>
            </form>
          </>
        ) : (
          <div style={S.noActiveChat}>
            <MessageSquare size={64} color="#1B5C3A" style={{ marginBottom: 16, opacity: 0.7 }} />
            <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, color: 'var(--char)', marginBottom: 8 }}>Your Conversations</h3>
            <p style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 280 }}>
              Select a conversation from the sidebar list to view messages and coordinate order details real-time.
            </p>
          </div>
        )}
      </div>

      {/* Web Premium Media Preview Modal */}
      {previewMedia && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.95)',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: 'sans-serif'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 24px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(0, 0, 0, 0.5)'
          }}>
            <span style={{
              color: '#fff',
              fontSize: '16px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: '80%'
            }}>
              {previewMedia.name}
            </span>
            <button
              onClick={() => setPreviewMedia(null)}
              style={{
                background: 'none',
                border: 'none',
                color: '#fff',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'opacity 0.2s',
              }}
            >
              ✕
            </button>
          </div>

          {/* Content Container */}
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            overflow: 'hidden'
          }}>
            {previewMedia.type === 'image' && (
              <img
                src={encodeURI(previewMedia.url)}
                alt={previewMedia.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                }}
              />
            )}

            {previewMedia.type === 'video' && (
              <video
                src={encodeURI(previewMedia.url)}
                controls
                autoPlay
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  borderRadius: '4px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                }}
              />
            )}

            {previewMedia.type === 'document' && (
              <iframe
                src={
                  previewMedia.url.toLowerCase().endsWith('.txt')
                    ? encodeURI(previewMedia.url)
                    : previewMedia.url.toLowerCase().endsWith('.pdf') ||
                      previewMedia.url.toLowerCase().includes('.doc') ||
                      previewMedia.url.toLowerCase().includes('.xls') ||
                      previewMedia.url.toLowerCase().includes('.ppt')
                      ? `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(previewMedia.url)}`
                      : encodeURI(previewMedia.url)
                }
                style={{
                  width: '100%',
                  height: '100%',
                  maxWidth: '1000px',
                  border: 'none',
                  borderRadius: '8px',
                  background: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                }}
                title={previewMedia.name}
              />
            )}
          </div>
        </div>
      )}

      {/* Modern Confirmation Modal */}
      {deleteTarget && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '12px',
            width: '90%',
            maxWidth: '350px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              color: '#DC2626'
            }}>
              <Trash2 size={30} />
            </div>
            <h3 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: '22px',
              fontWeight: 700,
              color: '#1A1A1A',
              marginBottom: '12px'
            }}>
              {deleteTarget.title}
            </h3>
            <p style={{
              fontSize: '14px',
              color: 'var(--muted)',
              lineHeight: '1.6',
              marginBottom: '24px'
            }}>
              {deleteTarget.desc}
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setDeleteTarget(null)}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '9px',
                  border: '1.5px solid var(--border)',
                  background: '#fff',
                  color: 'var(--muted)',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '12px 20px',
                  borderRadius: '9px',
                  border: 'none',
                  background: '#DC2626',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  opacity: deleting ? 0.6 : 1,
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#B91C1C')}
                onMouseLeave={(e) => !deleting && (e.currentTarget.style.backgroundColor = '#DC2626')}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
